import { useState } from 'react'
import { Link, useParams } from '@tanstack/react-router'

import { Badge } from '@/components/ui/Badge'
import { Select } from '@/components/ui/Controls'
import { PageHeader, Panel } from '@/components/ui/Panel'
import { EmptyState, QueryBoundary, Skeleton, TableSkeleton } from '@/components/ui/States'
import {
  useRepository,
  useRepositoryIssues,
  useRepositoryLabels,
} from '@/features/repositories/useRepositories'
import { formatRelative } from '@/lib/utils/format'

const STATE_OPTIONS = [
  { value: 'open', label: 'open' },
  { value: 'closed', label: 'closed' },
  { value: 'all', label: 'all' },
]

export function RepositoryDetailPage() {
  const { owner, repo } = useParams({ from: '/repositories/$owner/$repo' })
  const [state, setState] = useState<'open' | 'closed' | 'all'>('open')

  const detail = useRepository(owner, repo)
  const issues = useRepositoryIssues(owner, repo, state)
  const labels = useRepositoryLabels(owner, repo)

  return (
    <div>
      <PageHeader
        title={repo}
        description={detail.data?.description ?? `${owner}/${repo}`}
        actions={
          <Link to="/repositories" className="text-xs text-primary underline-offset-2 hover:underline">
            All repositories
          </Link>
        }
      />

      <QueryBoundary
        isLoading={detail.isLoading}
        error={detail.error}
        onRetry={() => void detail.refetch()}
        loadingFallback={<Skeleton className="h-24 w-full" />}
      >
        {detail.data ? (
          <Panel>
            <dl className="grid grid-cols-2 divide-line sm:grid-cols-4 sm:divide-x">
              <div className="px-4 py-3">
                <dt className="text-xs text-ink-muted">Visibility</dt>
                <dd className="mono text-ink-dim">{detail.data.private ? 'private' : 'public'}</dd>
              </div>
              <div className="px-4 py-3">
                <dt className="text-xs text-ink-muted">Default branch</dt>
                <dd className="mono text-ink-dim">{detail.data.default_branch ?? '—'}</dd>
              </div>
              <div className="px-4 py-3">
                <dt className="text-xs text-ink-muted">Language</dt>
                <dd className="text-ink-dim">{detail.data.language ?? '—'}</dd>
              </div>
              <div className="px-4 py-3">
                <dt className="text-xs text-ink-muted">Last push</dt>
                <dd className="text-ink-dim">{formatRelative(detail.data.pushed_at)}</dd>
              </div>
            </dl>
          </Panel>
        ) : null}
      </QueryBoundary>

      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Panel
          title="Issues"
          description={
            issues.data ? `${issues.data.total} ${state} — pull requests excluded` : undefined
          }
          actions={
            <Select
              label="state"
              options={STATE_OPTIONS}
              value={state}
              onChange={(event) => setState(event.target.value as 'open' | 'closed' | 'all')}
            />
          }
        >
          <QueryBoundary
            isLoading={issues.isLoading}
            error={issues.error}
            onRetry={() => void issues.refetch()}
            isEmpty={(issues.data?.items.length ?? 0) === 0}
            loadingFallback={<TableSkeleton rows={6} columns={3} />}
            emptyState={
              <EmptyState
                title={`No ${state} issues.`}
                hint={
                  state === 'open'
                    ? 'Nothing is currently open in this repository.'
                    : 'Try a different state.'
                }
              />
            }
          >
            <ul className="divide-y divide-line/60">
              {(issues.data?.items ?? []).map((issue) => (
                <li key={issue.number} className="flex items-start gap-3 px-4 py-2.5">
                  <span className="mono w-12 shrink-0 pt-0.5 text-ink-faint">#{issue.number}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-ink">{issue.title}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      {issue.labels.map((label) => (
                        <Badge key={label} tone="neutral">
                          {label}
                        </Badge>
                      ))}
                      <span className="text-xs text-ink-faint">
                        updated {formatRelative(issue.updated_at)}
                      </span>
                      {issue.assignees.length > 0 ? (
                        <span className="mono text-ink-faint">@{issue.assignees[0]}</span>
                      ) : null}
                    </div>
                  </div>
                  <Badge tone={issue.state === 'open' ? 'ok' : 'neutral'}>{issue.state}</Badge>
                </li>
              ))}
            </ul>
          </QueryBoundary>
        </Panel>

        <Panel
          title="Labels"
          description="Check these before asking the assistant to apply one"
        >
          <QueryBoundary
            isLoading={labels.isLoading}
            error={labels.error}
            onRetry={() => void labels.refetch()}
            isEmpty={(labels.data?.items.length ?? 0) === 0}
            loadingFallback={<TableSkeleton rows={4} columns={2} />}
            emptyState={<EmptyState title="No labels defined." />}
          >
            <ul className="divide-y divide-line/60">
              {(labels.data?.items ?? []).map((label) => (
                <li key={label.name} className="flex items-start gap-2.5 px-4 py-2.5">
                  <span
                    className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full border border-line-strong"
                    style={label.color ? { backgroundColor: `#${label.color}` } : undefined}
                    aria-hidden="true"
                  />
                  <div className="min-w-0">
                    <p className="mono text-ink-dim">{label.name}</p>
                    {label.description ? (
                      <p className="text-xs text-ink-muted">{label.description}</p>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </QueryBoundary>
        </Panel>
      </div>
    </div>
  )
}
